import React, { useContext } from 'react'
import styled from 'styled-components'

import CornerControls from './common/CornerControls'
import DetailCardLayout from './common/DetailCardLayout'
import TransportTimeline from './common/TransportTimeline'
import ItemTitle from './common/ItemTitle'
import NotesAndAttachments from './common/NotesAndAttachments'
import { DetailItemCardVariant } from './common/types'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import LineArrowRightIcon from 'components/Luxkit/Icons/line/LineArrowRightIcon'
import BodyText from 'components/Luxkit/Typography/BodyText'
import TripContext from 'tripPlanner/contexts/TripContext'
import { TransportItem } from 'tripPlanner/types/tripItem'
import { ItemMetadata } from 'tripPlanner/models'
import { formatSingleDateAndTimeRange } from 'tripPlanner/utils'
import { useRegionTimeFormat } from 'hooks/useRegionTimeFormat'
import { NarrowCardOnlyBookedLabel } from './common/BookedLabel'
import { NarrowCardOnlyJustAddedLabel } from './common/JustAddedLabel'
import { useJustAddedTripItem } from 'tripPlanner/hooks/useJustAddedTripItem'
import Group from 'components/utils/Group'

const TimelineContainer = styled.div`
  clear: both; /* Make sure the timeline sits below the corner buttons */
`

interface Props {
  item: TransportItem
  variant: DetailItemCardVariant
  tripNameLabel?: React.ReactNode
}

function DetailTransportCard({ item, variant, tripNameLabel }: Props) {
  const tripContext = useContext(TripContext)
  const canEdit = tripContext?.permissions.canEdit ?? false
  const { type, startPlace, endPlace } = item
  const { icon: Icon, singularName } = ItemMetadata[type]

  const title = endPlace ? (
    <>
      {startPlace.name} <LineArrowRightIcon size="S" /> {endPlace.name}
    </>
  ) : (
    `${singularName} from ${startPlace.name}`
  )

  const shouldShowTimeline = (
    (item.startDate && item.endDate || item.startDay && item.endDay)
  )

  const timeFormat = useRegionTimeFormat()
  const timeDescription = shouldShowTimeline ? undefined : formatSingleDateAndTimeRange(item, timeFormat)

  const details = [
    timeDescription,
    item.operatorName,
  ]
    .filter(Boolean)
    .join(' • ')

  const recentlyAdded = useJustAddedTripItem(item)

  return (
    <DetailCardLayout data-testid="detail-card-transport">
      <DetailCardLayout.Image></DetailCardLayout.Image>
      <DetailCardLayout.Content>
        {canEdit && <CornerControls item={item} variant={variant} />}
        <Group direction="horizontal" gap={4}>
          {item.isBooked && <NarrowCardOnlyBookedLabel className="bottom-margin" />}
          {recentlyAdded && <NarrowCardOnlyJustAddedLabel className="bottom-margin" />}
        </Group>
        <VerticalSpacer gap={8}>
          <VerticalSpacer gap={4}>
            <ItemTitle icon={Icon}>{title}</ItemTitle>
            {details && (
              <BodyText variant="medium" colour="neutral-three">
                {details}
              </BodyText>
            )}
          </VerticalSpacer>
          {shouldShowTimeline && <TimelineContainer>
            <TransportTimeline item={item} variant={variant} />
          </TimelineContainer>}
          {tripNameLabel}
          <NotesAndAttachments
            item={item}
            canEdit={canEdit}
            variant={variant}
          />
        </VerticalSpacer>
      </DetailCardLayout.Content>
    </DetailCardLayout>
  )
}

export default DetailTransportCard
